import React from 'react';
import styled from '@emotion/styled';
import { TabGalleryImage } from '@model/common/image';
import { ThemeProps } from '@theme/base';
import { createImageRows } from './GalleryTabs';
import { getImageUrl } from '@util/images';
import { breakpoints } from '@styles/breakpoints';

const ImageRow = styled.div({
  display: 'flex',
  gap: '10px',
  width: '100%'
});

const ImageContainer = styled.div<{ isFullWidth?: boolean }>(({ theme, isFullWidth }) => ({
  position: 'relative',
  width: isFullWidth ? '100%' : 'calc(50% - 5px)',
  aspectRatio: isFullWidth ? '16/9' : '3/2',
  borderRadius: '10px',
  overflow: 'hidden',
  backgroundColor: theme.custom.colors.group4.lighter,
  flex: isFullWidth ? '1 1 100%' : '1 1 calc(50% - 5px)'
}));

const GalleryImage = styled.img(({ theme }: ThemeProps) => ({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  transition: `transform ${theme.custom.transitions.default}`,

  ['&:hover']: {
    transform: 'scale(1.05)'
  }
}));

const TabContentWrapper = styled.div(({ theme }: ThemeProps) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  width: '100%',
  overflowY: 'auto',
  padding: theme.custom.spacing.medium
}));

const ImageGalleryInnerContainer = styled.div(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  width: '100%',
  overflow: 'auto',
  scrollbarWidth: 'none',
  ['&::-webkit-scrollbar']: {
    display: 'none'
  }
}));

export interface GalleryImageGridProps {
  filteredImages: Array<TabGalleryImage>;
  variant?: 'tab' | 'fullscreen';
}

export const GalleryImageGrid: React.FC<GalleryImageGridProps> = ({ filteredImages, variant = 'tab' }) => {
  const rows = createImageRows(filteredImages);

  const Container = variant === 'tab' ? TabContentWrapper : ImageGalleryInnerContainer;

  return (
    <Container>
      {rows.map((row, rowIndex) => {
        const isOddRow = rowIndex % 2 === 0;

        return (
          <ImageRow key={rowIndex}>
            {row.map((image, imageIndex) => (
              <ImageContainer key={`${image.url}-${rowIndex}-${imageIndex}`} isFullWidth={isOddRow}>
                <GalleryImage
                  src={getImageUrl(image.url, breakpoints.medium)}
                  alt={image.title || `Gallery image ${rowIndex * 2 + imageIndex + 1}`}
                  loading="lazy"
                />
              </ImageContainer>
            ))}
          </ImageRow>
        );
      })}
    </Container>
  );
};
